/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/

import {Injectable} from '@angular/core';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {SpecialOrderService} from '../../../api/ms-specialorders/specialorder/specialorder.service';
import {ConfirmModalComponent} from '../../../shared/confirm-modal/deprecated-modals/confirm-modal.component';
import {AlertNotificationSeverity} from '../../../alert-notification-severity.model';
import {ConfirmModal} from '../../../shared/confirm-modal/deprecated-modals/confirm-modal.model';
import {ToastWrapperService} from '../../../shared/utils/toast-wrapper-service/toast-wrapper-service';
import {Router} from '@angular/router';
import {Subject} from 'rxjs';
import {SpoCreateNewService} from '../../../api/ms-specialorders/specialorder/spo-create-new.service';
import {MultiStatusModel} from '../../../api/generic/contracts/multi-status.model';
import {CreateAndSubmitActionsEnum} from '../../../api/generic/contracts/create-and-submit-actions.enum';

@Injectable({
    providedIn: 'root'
})
export class SpoActionsUtilService {

    loadingSubject = new Subject<boolean>();
    enableLoading = this.loadingSubject.asObservable();

    private readonly SUBMIT_MODAL_WIDTH = 615;

    constructor(private specialOrderService: SpecialOrderService,
                private sendSpoService: SpoCreateNewService,
                private modalService: NgbModal,
                private toastrService: ToastWrapperService,
                private router: Router) {
    }

    /**
     * Deletes a SPO, opening a confirmation modal before the backend request
     * @param number The SPO number to be deleted
     * @param successCallback A successCallback to be called when the backend request returns a success status
     */
    deleteSpecialOrder(number: string, successCallback: Function = (): void => {
    }): void {
        const confirmModalInstance: ConfirmModalComponent = this.modalService.open(ConfirmModalComponent, {
            centered: true
        }).componentInstance;
        confirmModalInstance.initModal({
            titleText: 'Delete',
            subtitleText: `Are you sure you want to delete draft SPO number ${number}?`,
            alertNotificationSeverity: AlertNotificationSeverity.ERROR,
            primaryButtonText: 'Delete',
            primaryButtonCustomizedClass: 'btn-primary-red',
            secondaryButtonText: 'Cancel',
            manualChecks: {
                title: `<strong>Warning:</strong> This action cannot be undone`,
                inputs: [
                    {
                        checked: false,
                        message: 'All header and material information will be deleted'
                    },
                    {
                        checked: false,
                        message: 'All activity information will be dismissed'
                    }
                ]
            }
        } as ConfirmModal).confirmEvent.subscribe(
                () => {
                    this.specialOrderService.deleteSpecialOrder(number)
                            .subscribe(() => {
                                successCallback();
                                this.toastrService.success(`${number} <strong>deleted</strong> successfully`, 'Success');
                            }, error => {
                                this.toastrService.error(`An error has occurred. ${error}`, 'Error');
                            });
                }
        );
    }

    /**
     * Determines if a SPO must be submitted or partially submitted
     * @param number The SPO number to be submitted
     * @param materialCount The amount of materials to be submitted
     * @param canSubmit If the SPO should be submitted
     * @param canRetry If the SPO should be partially submitted
     * @param successCallback A successCallback to be called when the backend request returns a success status
     */
    submitSpecialOrder(number: string,
                       materialCount: number,
                       canSubmit: boolean,
                       canRetry: boolean,
                       successCallback: Function = (): void => {
                       }): void {
        if (canRetry) {
            this.sendSpecialOrderOnlyDraftMaterials(number, materialCount, successCallback);
        } else if (canSubmit) {
            this.sendSpecialOrder(number, materialCount, successCallback);
        }
    }

    /**
     * Determines the correct label for the send button in context menu
     * @param canRetry If the special order available actions includes RESUBMIT
     * @return string The send button correct label
     */
    getContextMenuSendButtonText(canRetry: boolean): string {
        if (canRetry) {
            return 'Send drafts only';
        }
        return 'Send';
    }

    /**
     * Returns the multi-status toast error message to the user
     * @param successUpdatedMaterials the number of materials updated with success
     * @param materialsToUpdate the number of materials updated
     * @param number the SPO number
     * @param materialUpdateErrorMessage the API error from material update
     * @param action the action over the materials
     */
    getMultiStatusToastErrorMessage(successUpdatedMaterials: number,
                                    materialsToUpdate: number,
                                    number: string,
                                    materialUpdateErrorMessage: string,
                                    action: 'updated' | 'submitted'): string {
        const firstSentence = successUpdatedMaterials > 0
                ? `${materialsToUpdate - successUpdatedMaterials} of ${materialsToUpdate} materials`
                : `The material(s)`;

        return `<p>${firstSentence} of the SPO <strong>${number}</strong>` +
                ` weren't ${action} because of an unexpected error.</p><br>` +
                `<p><strong>Please, try again later.</strong></p><br>` +
                `<p>"${materialUpdateErrorMessage}"</p>`;
    }

    /**
     * Submits an existing SPO, opening a confirmation modal before the backend request is triggered
     * @param number The SPO number to be submitted
     * @param materialCount The amount of materials to be submitted
     * @param successCallback A successCallback to be called when the backend request returns a success status
     */
    private sendSpecialOrder(number: string, materialCount: number, successCallback: Function = (): void => {
    }): void {
        const confirmModalInstance: ConfirmModalComponent = this.modalService.open(ConfirmModalComponent, {
            centered: true
        }).componentInstance;
        confirmModalInstance.initModal({
            titleText: 'Confirm',
            subtitleText: 'Are you sure you want to send the SPO?',
            alertNotificationSeverity: AlertNotificationSeverity.INFORMATION,
            primaryButtonText: 'Send',
            secondaryButtonText: 'Cancel',
            width: this.SUBMIT_MODAL_WIDTH,
            manualChecks: {
                title: `<strong>Be aware that:</strong>`,
                inputs: [
                    {
                        checked: false,
                        message: 'This action will send the SPO to BMW and the process will be started'
                    },
                    {
                        checked: false,
                        message: 'Edit and Cancel options for open SPO are only available for Materials with "submitted" status'
                    }
                ]
            }
        } as ConfirmModal).confirmEvent.subscribe(() => {
            this.loadingSubject.next(true);
            this.sendSpoService.sendSavedSpo(number).subscribe(
                    response => {
                        if (response.status === 207) {
                            const multiStatus = response.body;
                            const submitAction = multiStatus.actions
                                    .filter(status => status.action === CreateAndSubmitActionsEnum.SUBMIT && status.error == null);

                            const successCount = submitAction.length;
                            const errorAction = multiStatus.actions.find(s => s.error != null);
                            const spoNumber = errorAction.identifier.substr(0, errorAction.identifier.indexOf('-'));

                            this.toastrService.error(this.getMultiStatusToastErrorMessage(successCount,
                                    materialCount, spoNumber, errorAction.error.message, 'submitted'), 'Error');
                        } else {
                            this.toastrService.success(
                                    `SPO No.
                                            <span class="toast-new-spo-number">${number}</span>
                                            <strong>sent</strong> successfully`,
                                    'Success');
                            successCallback();
                        }
                    }).add(() => {
                this.loadingSubject.next(false);
            });
        });
    }

    /**
     * Resubmits an existing SPO draft materials
     * @param number The SPO number to resubmit
     * @param materialCount The amount of materials to be submitted
     * @param successCallback A successCallback to be called when the backend request returns a success status
     */
    private sendSpecialOrderOnlyDraftMaterials(number: string, materialCount: number, successCallback: Function = (): void => {
    }): void {
        this.sendSpoService.resubmitSavedSpo(number).subscribe(response => {
            if (response.status === 207) {
                const multiStatus = response.body;
                const submitAction = multiStatus.actions
                        .filter(status => status.action === CreateAndSubmitActionsEnum.SUBMIT && status.error == null);

                const successCount = submitAction.length;
                const errorMessage = multiStatus.actions.find(s => s.error != null).error.message;

                this.toastrService.error(this.getMultiStatusToastErrorMessage(successCount,
                        materialCount, number, errorMessage, 'updated'), 'Error');
            } else {
                this.toastrService.success(`SPO No. <span class="toast-new-spo-number">${number}</span>` +
                        ` materials <strong>resubmitted</strong> successfully`, 'Success');
                successCallback();
            }
        });
    }
}
